import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Trash2,
  Share,
  Eye,
  FileUp,
  MoreVertical,
  RotateCw,
  X,
  Filter,
  Brain,
  Users,
  Clock,
  Edit,
  Copy,
  Star,
  Building2  
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { toast } from "sonner";
import { useUser } from "../../../../context/UserContext";
import { useCreatePermissions } from "../../../../hooks/useCreatePermissions";
import LockButton from "../../../Lock/LockButton";
import LockBadge from "../../../Lock/LockBadge";
import {
  listKnowledgeBases,
  listKnowledgeBasesPaginated,
  deleteKnowledgeBase,
} from "../../../../service/apiService";

// Define BrainBank type
interface BrainBank {
  id: string;
  knowledgebase_name: string;
  index_name: string;
  description: string;
  department?: string;
  file_ids: string[];
  // Counted server-side from the file rows; file_ids is stale and reads 0.
  file_count?: number;
  last_sync: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  summary: string | null;
  // Admin lock. Only ever set on rows the viewer locked themselves -- the API
  // hides everyone else's locked BrainBanks from this list entirely.
  locked_by?: string | null;
  locked_at?: string | null;
  files?: Array<{
    id: string;
    file_name: string;
    status: string;
    created_by: string;
    created_at: string;
  }>;
}

type ViewType = 'my_knowledge_base' | 'assigned_knowledge_base' | 'all';

interface MobileBBViewProps {
  onCreateClick: () => void;
  onViewFilesClick: (brainBank: BrainBank) => void;
  onUploadClick: (brainBank: BrainBank) => void;
  onEditClick: (brainBank: BrainBank) => void;  
  onDuplicateClick?: (brainBank: BrainBank) => void;
  refreshKey: number;
}

const MobileBBView: React.FC<MobileBBViewProps> = ({
  onCreateClick,
  onEditClick,
  onViewFilesClick,
  onUploadClick,
  refreshKey
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [brainBanks, setBrainBanks] = useState<BrainBank[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  
  const { userId, userDetails } = useUser();
  const isAdmin = userDetails?.role !== 'user';
  const { canCreateBrainBank } = useCreatePermissions();

  // Set default filter based on user role
  const [currentFilter, setCurrentFilter] = useState<ViewType>(
    isAdmin ? 'all' : 'my_knowledge_base'
  );
  const [tempFilter, setTempFilter] = useState<ViewType>(
    isAdmin ? 'all' : 'my_knowledge_base'
  );
  
  // Load BrainBanks
  useEffect(() => {
    if (userId) {
      fetchBrainBanks();
    }
  }, [refreshKey, currentFilter, isAdmin, userId]);

  const fetchBrainBanks = async () => {
    setLoading(true);
    try {
      if (isAdmin && currentFilter === 'all') {
        // Admin sees all BrainBanks
        const result = await listKnowledgeBases();
        setBrainBanks(Array.isArray(result) ? result : []);
      } else {
        // Regular users see filtered BrainBanks
        const result = await listKnowledgeBasesPaginated(1, 100, currentFilter as 'my_knowledge_base' | 'assigned_knowledge_base');
        setBrainBanks(result?.knowledge_bases || []);
      }
    } catch (error) {
      console.error("Error fetching BrainBanks:", error);
      toast.error("Failed to load BrainBanks");
      setBrainBanks([]);
    } finally {
      setLoading(false);
      setIsFilterLoading(false);
    }
  };
  
  // Filtered BrainBanks based on search
  const filteredBrainBanks = React.useMemo(() => {
    if (!searchQuery.trim()) return brainBanks;
    
    const query = searchQuery.toLowerCase();
    return brainBanks.filter((bb: BrainBank) => 
      bb.knowledgebase_name.toLowerCase().includes(query) || 
      bb.description?.toLowerCase().includes(query) ||
      bb.department?.toLowerCase().includes(query)
    );
  }, [brainBanks, searchQuery]);

  const getBrainBankType = (brainBank: BrainBank, currentUserId: string) => {
    const isOwner = brainBank.created_by === currentUserId;
    
    if (isOwner) {
      return "My BrainBank";
    } else {
      return "Assigned BrainBank";
    }
  };

  // Handle filter modal open
  const handleFilterClick = () => {
    setTempFilter(currentFilter);
    setShowFilterModal(true);
  };

  // Handle filter apply
  const handleApplyFilter = async () => {
    if (tempFilter !== currentFilter) {
      setIsFilterLoading(true);
      setShowFilterModal(false);
      
      try {
        setCurrentFilter(tempFilter);
        setSearchQuery(""); // Clear search when changing filters
      } catch (error) {
        console.error("Error applying filter:", error);
        toast.error("Failed to apply filter");
      }
    } else {
      setShowFilterModal(false);
    }
  };

  // Handle clear filters - updated to match desktop version
  const handleClearFilters = async () => {
    const defaultFilter: ViewType = isAdmin ? "all" : "my_knowledge_base";
    if (currentFilter !== defaultFilter) {
      setIsFilterLoading(true);
      setShowFilterModal(false);
      
      try {
        setTempFilter(defaultFilter);
        setCurrentFilter(defaultFilter);
        setSearchQuery("");
      } catch (error) {
        console.error("Error clearing filters:", error);
        toast.error("Failed to clear filters");
      }
    } else {
      setTempFilter(defaultFilter);
      setShowFilterModal(false);
    }
  };

  // Get filter display text
  const getFilterDisplayText = () => {
    switch (currentFilter) {
      case 'my_knowledge_base':
        return 'My BrainBanks';
      case 'assigned_knowledge_base':
        return 'Assigned BrainBanks';
      case 'all':
        return 'All BrainBanks';
      default:
        return 'All BrainBanks';
    }
  };

  // Handle clicking the action menu
  const toggleActionMenu = (brainBankId: string | undefined, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!brainBankId) return;
    setActionMenuOpen(actionMenuOpen === brainBankId ? null : brainBankId);
  };
  
  // Handle delete button click - shows confirmation UI
  const handleDeleteClick = (brainBank: BrainBank, e: React.MouseEvent) => {
    e.stopPropagation();
    if (brainBank.id) {
      setConfirmDeleteId(brainBank.id);
      setActionMenuOpen(null); // Close action menu
    } else {
      toast.error("Cannot delete: Missing BrainBank ID");
    }
  };
  
  // Handle confirm delete
  const handleDelete = async (brainBankId: string) => {
    if (!brainBankId) {
      toast.error("Error: No BrainBank selected for deletion");
      return;
    }
    
    setIsDeleting(true);
    try {
      await deleteKnowledgeBase(brainBankId);
      setConfirmDeleteId(null);
      toast.success("BrainBank deleted successfully");
      fetchBrainBanks(); // Refresh the list
    } catch (err: any) {
      console.error("Error deleting BrainBank:", err);
      if (err?.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error("Failed to delete BrainBank");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Close all action menus when clicking outside
  useEffect(() => {
    const handleOutsideClick = () => {
      setActionMenuOpen(null);
    };
    
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <div className="w-full p-3">
        {/* Header with search and create button */}
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-medium">BrainBanks</h2>
          {canCreateBrainBank && (
            <button
              onClick={onCreateClick}
              className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1 rounded-md text-sm font-medium flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Create New
            </button>
          )}
        </div>
        
        {/* Search Input */}
        <div className="relative mb-3">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            placeholder="Search BrainBanks"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Filter Button - Only show for non-admin users */}
        {!isAdmin && (
          <button
            onClick={handleFilterClick}
            disabled={isFilterLoading}
            className={`w-full flex items-center justify-between px-3 py-2 mb-3 text-sm border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500 ${
              isFilterLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isFilterLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-violet-600 mr-2"></div>
                <span>Loading...</span>
              </div>
            ) : (
              <>
                <span>{getFilterDisplayText()}</span>
                <Filter size={14} className="text-gray-400" />
              </>
            )}
          </button>
        )}
        
        {/* BrainBank List as Cards */}
        <div className="space-y-3">
          {loading || isFilterLoading ? (
            <div className="bg-white rounded-lg shadow-sm p-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                <span className="text-sm text-gray-500">
                  {isFilterLoading ? "Applying filter..." : "Loading BrainBanks..."}
                </span>
              </div>
            </div>
          ) : filteredBrainBanks.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-4 text-center">
              <Brain className="mx-auto h-12 w-12 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                {searchQuery 
                  ? "No BrainBanks match your search" 
                  : "No BrainBanks found"}
              </p>
            </div>
          ) : (
            filteredBrainBanks.map((brainBank) => (
              <div 
                key={brainBank.id} 
                className="bg-white rounded-lg shadow-sm p-3 border border-gray-100"
              >
                <div className="flex items-center justify-between">
                  <div 
                    className="font-medium text-gray-900 cursor-pointer flex items-center"
                    onClick={() => onViewFilesClick(brainBank)}
                  >
                    <Brain className="h-4 w-4 mr-2 flex-shrink-0 text-violet-600" />
                    <span className="truncate">{brainBank.knowledgebase_name}</span>
                    {brainBank.locked_by && (
                      <LockBadge
                        className="ml-2 flex-shrink-0"
                        label={
                          brainBank.locked_at
                            ? `Locked on ${new Date(brainBank.locked_at).toLocaleDateString()}`
                            : "Locked"
                        }
                      />
                    )}
                  </div>
                  
                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <div className="relative">
                      <button 
                        onClick={(e) => toggleActionMenu(brainBank.id, e)}
                        className="p-1 rounded-full focus:outline-none text-gray-500"
                        aria-label="More options"
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>
                        
                      {actionMenuOpen === brainBank.id && (
                        <div 
                          className="absolute right-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 z-10 p-1"
                          onClick={(e) => e.stopPropagation()}
                          style={{ minWidth: '140px' }}
                        >
                          <div className="flex items-center justify-around space-x-1">
                           <button 
                            className="p-2 rounded text-blue-600 hover:bg-gray-100"
                            onClick={() => onViewFilesClick(brainBank)}
                            aria-label="View Files"
                            title="View Files"
                            >
                            <Eye className="h-4 w-4" />
                          </button>
  
                          {/* Edit + Delete -- hidden when admin turned off BrainBank creation */}
                          {canCreateBrainBank && (
                          <>
                          <button
                           className="p-2 rounded text-violet-600 hover:bg-gray-100"
                           onClick={() => onEditClick(brainBank)}
                           aria-label="Edit BrainBank"
                           title="Edit BrainBank"
                           >
                          <Edit className="h-4 w-4" />
                        </button>
  
                        <button 
                         className="p-2 rounded text-red-600 hover:bg-gray-100"
                         onClick={(e) => handleDeleteClick(brainBank, e)}
                         aria-label="Delete"
                         title="Delete"
                        >
                         <Trash2 className="h-4 w-4" />
                       </button>
                          </>
                          )}

                       <LockButton
                         resourceType="brainbank"
                         resourceId={brainBank.id}
                         resource={brainBank}
                         resourceName={brainBank.knowledgebase_name}
                         ownerId={brainBank.created_by}
                         onChange={fetchBrainBanks}
                         className="p-2 rounded hover:bg-gray-100"
                       />
                     </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* BrainBank Type and Team Info */}
                <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                  {!isAdmin && <span>{getBrainBankType(brainBank, userId)}</span>}
                  <div className="flex items-center">
                    <Users className="h-3 w-3 mr-1" />
                    <span>Private Team</span>
                  </div>
                </div>

                {/* Department Section - NEW ADDITION */}
                <div className="flex items-center mt-2 mb-2">
                  {brainBank.department ? (
                    <>
                      <Building2 className="text-violet-600 mr-2 flex-shrink-0" size={14} />
                      <span className="text-sm text-gray-700">{brainBank.department}</span>
                    </>
                  ) : (
                    <div className="flex items-center">
                      <Building2 className="text-gray-400 mr-2 flex-shrink-0" size={14} />
                      <span className="text-sm text-gray-500 italic">No Department</span>
                    </div>
                  )}
                </div>
                
                {/* Description */}
                <div 
                  className="text-sm text-gray-700 mt-2 line-clamp-2 cursor-pointer"
                  onClick={() => onViewFilesClick(brainBank)}
                >
                  {brainBank.description || "No description available"}
                </div>

                {/* File Count and Last Sync */}
                <div className="flex items-center justify-between text-xs text-gray-400 mt-2">
                  <div className="flex items-center">
                    <FileUp className="h-3 w-3 mr-1" />
                    <span>
                      {typeof brainBank.file_count === "number"
                        ? `${brainBank.file_count} files`
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    <span>Updated {formatDate(brainBank.updated_at)}</span>
                  </div>
                </div>
                
                {/* Delete Confirmation */}
                {confirmDeleteId === brainBank.id && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                    <p className="text-sm text-gray-700 mb-2">
                      You will lose all data and files in this BrainBank.
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDelete(brainBank.id || "")}
                        className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 focus:outline-none"
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <div className="flex items-center">
                            <RotateCw className="animate-spin h-3 w-3 mr-1" />
                            <span>Deleting...</span>
                          </div>
                        ) : "Yes"}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="rounded-md bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300 focus:outline-none"
                      >
                        No
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Filter Modal - Only for non-admin users */}
      {!isAdmin && (
        <Transition appear show={showFilterModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowFilterModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex justify-between items-center mb-4">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        Filter BrainBanks
                      </Dialog.Title>
                      <button
                        onClick={() => setShowFilterModal(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="flex items-center text-sm font-medium text-gray-700 mb-3">
                          <Star size={16} className="mr-2 text-violet-500" />
                          Filters
                        </h4>
                        
                        <div className="space-y-2">
                          {/* Only show 'All BrainBanks' option for admins */}
                          {isAdmin && (
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="filter"
                                value="all"
                                checked={tempFilter === 'all'}
                                onChange={(e) => setTempFilter(e.target.value as ViewType)}
                                className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                              />
                              <span className="ml-2 text-sm text-gray-700">All BrainBanks</span>
                            </label>
                          )}
                          
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="filter"
                              value="my_knowledge_base"
                              checked={tempFilter === 'my_knowledge_base'}
                              onChange={(e) => setTempFilter(e.target.value as ViewType)}
                              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                            />
                            <span className="ml-2 text-sm text-gray-700">My BrainBanks</span>
                          </label>
                          
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="filter"
                              value="assigned_knowledge_base"
                              checked={tempFilter === 'assigned_knowledge_base'}
                              onChange={(e) => setTempFilter(e.target.value as ViewType)}
                              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                            />
                            <span className="ml-2 text-sm text-gray-700">Assigned BrainBanks</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-between">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                        onClick={handleClearFilters}
                      >
                        Clear all filters
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                        onClick={handleApplyFilter}
                      >
                        Done
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </>
  );
};

export default MobileBBView;